import { ConvexProvider, ConvexReactClient } from "convex/react";
import type { ReactNode } from "react";
import { configUrlErrorMessage, parseOptionalHttpUrl } from "../domain/config";

const convexUrlConfig = parseOptionalHttpUrl("VITE_CONVEX_URL", import.meta.env.VITE_CONVEX_URL);

export const convexClient =
  convexUrlConfig.ok && convexUrlConfig.value.kind === "configured"
    ? new ConvexReactClient(convexUrlConfig.value.url)
    : null;

/**
 * Wraps the app in a Convex provider. If `VITE_CONVEX_URL` isn't set yet
 * (before the backend is wired up), render a friendly notice instead of
 * crashing, so the build/preview still works offline.
 */
export function ConvexRoot({ children }: { children: ReactNode }) {
  if (!convexUrlConfig.ok) {
    return (
      <BackendNotice
        title="Backend configuration is invalid"
        body={configUrlErrorMessage(convexUrlConfig.error)}
      />
    );
  }

  if (convexUrlConfig.value.kind === "missing" || convexClient === null) {
    return (
      <BackendNotice
        title="Backend not connected"
        body="Set VITE_CONVEX_URL in .env.local, then restart the dev server."
      />
    );
  }

  return <ConvexProvider client={convexClient}>{children}</ConvexProvider>;
}

function BackendNotice({ title, body }: { title: string; body: string }) {
  return (
    <div className="mx-auto mt-24 max-w-lg rounded-lg border border-amber-300 bg-amber-50 p-6 text-amber-900">
      <h1 className="text-lg font-semibold">{title}</h1>
      <p className="mt-2 text-sm">{body}</p>
    </div>
  );
}
